import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BottomTabBar } from '@/components/layout/BottomTabBar'
import { formatShortThaiDate } from '@krudream/shared'
import { apiUrl } from '@/lib/api'

interface StudentRow {
  studentId: string
  studentNumber: number
  prefix: string
  fullName: string
  nickname: string | null
  submitted: boolean
  missingCount: number
  linked: boolean
}

interface DashboardData {
  event: {
    id: string
    name: string
    date: string
  }
  totalStudents: number
  submittedCount: number
  missingItemsCount: number
  notSubmittedCount: number
  notLinkedCount: number
  students: StudentRow[]
}

type FilterTab = 'all' | 'missing' | 'not_submitted' | 'not_linked'

export function OrientationDashboardPage() {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<DashboardData | null>(null)
  const [filter, setFilter] = useState<FilterTab>('all')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 2000)
  }

  useEffect(() => {
    if (!eventId) return
    fetch(apiUrl(`/api/orientation/events/${eventId}/summary`), { credentials: 'include' })
      .then((r) => r.json())
      .then((res) => {
        if (res.success) {
          const d = res.data
          setData({
            ...d,
            missingItemsCount: d.students.filter((s: StudentRow) => s.missingCount > 0).length,
            notSubmittedCount: d.students.filter((s: StudentRow) => !s.submitted).length,
            notLinkedCount: d.students.filter((s: StudentRow) => !s.linked).length,
          })
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }, [eventId])

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-400">กำลังโหลด...</div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-400">ไม่พบข้อมูล</div>
      </div>
    )
  }

  const filtered = data.students.filter((s) => {
    if (filter === 'missing') return s.missingCount > 0
    if (filter === 'not_submitted') return !s.submitted
    if (filter === 'not_linked') return !s.linked
    return true
  })

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    if (selected.size === filtered.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(filtered.map((s) => s.studentId)))
    }
  }

  async function handleNotify() {
    if (selected.size === 0) return
    setIsSending(true)
    try {
      const res = await fetch(apiUrl(`/api/notifications/orientation/${eventId}/notify`), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ studentIds: [...selected] }),
      })
      const resData = await res.json()
      showToast(resData.message || `ส่งแจ้งเตือน ${selected.size} คนแล้ว`)
      setSelected(new Set())
    } catch {
      showToast('เกิดข้อผิดพลาด')
    } finally {
      setIsSending(false)
    }
  }

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'all', label: 'ทั้งหมด' },
    { key: 'missing', label: 'ของขาด' },
    { key: 'not_submitted', label: 'ไม่ submit' },
    { key: 'not_linked', label: 'ไม่ link' },
  ]

  const metrics = [
    { label: 'ครบ', value: data.submittedCount - data.missingItemsCount, color: 'bg-green-50 text-green-700' },
    { label: 'ของขาด', value: data.missingItemsCount, color: 'bg-amber-50 text-amber-700' },
    { label: 'ยังไม่ submit', value: data.notSubmittedCount, color: 'bg-blue-50 text-blue-700' },
    { label: 'link ไม่ได้', value: data.notLinkedCount, color: 'bg-red-50 text-red-700' },
  ]

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <button onClick={() => navigate(`/orientation/${eventId}`)} className="text-gray-600">
            ⟨
          </button>
          <div className="flex-1">
            <h1 className="truncate text-base font-bold text-gray-900">{data.event.name}</h1>
            <p className="text-xs text-gray-400">
              {formatShortThaiDate(new Date(data.event.date))}
            </p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-3">
        {/* Metric cards */}
        <div className="mb-3 grid grid-cols-2 gap-2">
          {metrics.map((m) => (
            <div key={m.label} className={`rounded-lg p-3 ${m.color}`}>
              <p className="text-2xl font-bold">{m.value}</p>
              <p className="text-xs">{m.label}</p>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div className="mb-3 flex gap-2 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setFilter(tab.key)
                setSelected(new Set())
              }}
              className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium ${
                filter === tab.key
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 shadow-sm'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Select all */}
        {filtered.length > 0 && (
          <label className="mb-2 flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={selected.size === filtered.length && filtered.length > 0}
              onChange={toggleSelectAll}
              className="rounded"
            />
            เลือกทั้งหมด ({filtered.length})
          </label>
        )}

        {/* Student list */}
        <div className="space-y-1">
          {filtered.map((s) => (
            <label
              key={s.studentId}
              className="flex w-full cursor-pointer items-center gap-3 rounded-lg bg-white px-3 py-2.5 text-sm shadow-sm"
            >
              <input
                type="checkbox"
                checked={selected.has(s.studentId)}
                onChange={() => toggleSelect(s.studentId)}
                className="rounded"
              />
              <span className="flex-1 text-gray-900">
                {s.studentNumber} · {s.prefix}{s.fullName}
                {s.nickname && <span className="text-gray-400"> ({s.nickname})</span>}
              </span>
              <span className="flex items-center gap-1.5">
                {!s.linked && (
                  <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-600">
                    ไม่ link
                  </span>
                )}
                {!s.submitted && (
                  <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-600">
                    ไม่ submit
                  </span>
                )}
                {s.submitted && s.missingCount > 0 && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                    ขาด {s.missingCount}
                  </span>
                )}
                {s.submitted && s.missingCount === 0 && (
                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-600">
                    ครบ
                  </span>
                )}
              </span>
            </label>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-400 shadow-sm">
            ไม่พบนักเรียนในหมวดนี้
          </div>
        )}

        {/* Notify button */}
        {selected.size > 0 && (
          <div className="mt-4">
            <button
              onClick={handleNotify}
              disabled={isSending}
              className="w-full rounded-lg bg-primary-600 py-3 text-sm font-semibold text-white disabled:opacity-50"
            >
              {isSending ? 'กำลังส่ง...' : `แจ้งเตือนที่เลือก (${selected.size} คน)`}
            </button>
          </div>
        )}
      </main>

      {toast && (
        <div className="fixed left-1/2 top-4 z-50 -translate-x-1/2 rounded-lg bg-gray-800 px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <BottomTabBar active="more" />
    </div>
  )
}
